import type { CalibrationBin } from '../../api'

/**
 * How the Model page writes a number down.
 *
 * Split from `parts.tsx` rather than living beside the components, because a module that exports
 * both components and plain functions loses React Fast Refresh for the whole file — the lint rule
 * says so, and on a page whose panels are being tuned by eye that is a real cost.
 */

/**
 * Mean absolute gap between what was claimed and what happened, weighted by bin size.
 *
 * Weighted deliberately: an unweighted mean lets a bin holding one match move the headline as much
 * as a bin holding forty, which is how a reliability number ends up describing noise.
 */
export function calibrationError(bins: CalibrationBin[]): number {
  const filled = bins.filter((b) => b.count > 0 && b.predicted !== null && b.actual !== null)
  const total = filled.reduce((sum, b) => sum + b.count, 0)
  if (total === 0) return 0
  return filled.reduce((sum, b) => sum + b.count * Math.abs(b.predicted! - b.actual!), 0) / total
}

/**
 * An ISO timestamp in the same shape as {@link date}, plus the clock.
 *
 * The model card used to print `8/15/2026, 10:40:28` — locale-short, with seconds — a hundred pixels
 * above a history printing `Aug 15, 2026`. Two formats for the same kind of fact. Seconds are noise
 * on a training cutoff; the HOURS are not, and never were: the first shipped model claimed a cutoff
 * before the tournament while its real one sat eight hours into day one, which is the mistake this
 * page exists to make visible.
 */
export function dateTime(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.valueOf())) return '—'
  return `${at.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}, ${at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}`
}

/** Unix seconds to a plain date. Times are noise in a history spanning days. */
export function date(seconds: number): string {
  if (!seconds) return '—'
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** A probability as whole percentage points. */
export function percent(value: number, places = 0): string {
  return `${(value * 100).toFixed(places)}%`
}

/**
 * A number that may be positive or negative, always carrying its sign.
 *
 * The sign is the entire message on the market page — it says which of two forecasters is ahead —
 * and a bare "0.004" beside "theirs minus ours" makes the reader do the subtraction themselves.
 *
 * **A value that rounds to nothing prints without a sign.** A row where the two forecasts were
 * identical rendered as "−0.000", which claims a direction the number does not have and, worse,
 * claims it from a rounding artefact: whether it came out "+0.000" or "−0.000" depended on the last
 * bit of a float. The test is against the printed precision, not against zero.
 */
export function signed(value: number, places: number): string {
  const magnitude = Math.abs(value)
  if (magnitude < 0.5 * 10 ** -places) {
    return (0).toFixed(places)
  }

  return `${value > 0 ? '+' : '−'}${magnitude.toFixed(places)}`
}
